import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion, useMotionTemplate, useMotionValue, useReducedMotion, useSpring } from "framer-motion";
import { cn } from "@/lib/utils";

export default function TiltCard({ children, className, max = 8, glare = true, resetKey, ...props }) {
  const cardRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();
  const [canHover, setCanHover] = useState(false);
  const rotateX = useMotionValue(0);
  const rotateY = useMotionValue(0);
  const glareX = useMotionValue(50);
  const glareY = useMotionValue(50);
  const smoothX = useSpring(rotateX, { stiffness: 200, damping: 18, mass: 0.35 });
  const smoothY = useSpring(rotateY, { stiffness: 200, damping: 18, mass: 0.35 });
  const transform = useMotionTemplate`perspective(1000px) rotateX(${smoothX}deg) rotateY(${smoothY}deg)`;
  const glareBackground = useMotionTemplate`radial-gradient(circle at ${glareX}% ${glareY}%, rgba(201, 162, 39, 0.32), transparent 55%)`;
  const enabled = canHover && !prefersReducedMotion;

  useLayoutEffect(() => {
    rotateX.jump(0);
    rotateY.jump(0);
    smoothX.jump(0);
    smoothY.jump(0);
    glareX.set(50);
    glareY.set(50);
  }, [resetKey, rotateX, rotateY, smoothX, smoothY, glareX, glareY]);

  useEffect(() => {
    const query = window.matchMedia("(hover: hover) and (pointer: fine)");
    const update = () => setCanHover(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  const handleMouseMove = (event) => {
    if (!enabled || !cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;
    rotateX.set((0.5 - y) * max);
    rotateY.set((x - 0.5) * max);
    glareX.set(x * 100);
    glareY.set(y * 100);
  };

  const handleMouseLeave = () => {
    rotateX.set(0);
    rotateY.set(0);
  };

  return (
    <motion.div
      ref={cardRef}
      className={cn("relative overflow-hidden rounded-xl", className)}
      style={enabled ? { transform, transformStyle: "preserve-3d" } : undefined}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      {...props}
    >
      {children}
      {glare && enabled && <motion.div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{ background: glareBackground }} />}
    </motion.div>
  );
}
